'use client';

import { c as _c } from "react/compiler-runtime";
import { parseMarketConfig, useGetProposalField, useMarketConfig } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { EntityTag } from '@/components/search/entity-tag';
import { RowAside, TickerText } from '@/components/search/search-row-parts';
import { tickerLabel } from '@/components/search/search-row-parts.utils';
import { SearchRowSubline } from '@/components/search/search-row-subline';
import { useCommandLink } from '@/components/search/use-command-link';
import { CommandItem } from '@/components/ui/app-command';
import { useFormattedCurrency } from '@/lib/currency';
interface ProposalRowSearchItemProps extends MarketIdentityData {
  proposalId: string;
  title: string;
  coverImageUrl: string;
  coverImageNsfw: boolean;
  totalFunded: string;
  marketConfig: string | undefined;
  numOutcomes: number;
  backerCount: number;
  ticker: string;
  tickerHighlighted?: boolean;
  href: string;
  onCommit: () => void;
  onHoverPrefetch?: (proposalId: string) => void;
}
function safeBigInt(value: string | undefined): bigint | null {
  if (value === undefined || value === '') return null;
  try {
    return BigInt(value);
  } catch {
    // SILENT-CATCH: non-numeric strings → treat as absent
    return null;
  }
}
export function ProposalRowSearchItem(props) {
  const $ = _c(54);
  let t0;
  if ($[0] !== props.proposalId) {
    t0 = {
      pubkey: props.proposalId
    };
    $[0] = props.proposalId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const liveTotalFunded = useGetProposalField(t0, "totalFunded");
  const config = useMarketConfig(props.marketConfig);
  const t = useTranslations("search");
  const {
    formatD6
  } = useFormattedCurrency();
  const funded = liveTotalFunded ?? props.totalFunded;
  const fundedBig = safeBigInt(funded);
  let t1;
  if ($[2] !== config) {
    t1 = config === undefined ? null : parseMarketConfig(config).creationThresholdD6;
    $[2] = config;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const goalBig = t1;
  let t2;
  if ($[4] !== config || $[5] !== formatD6 || $[6] !== funded || $[7] !== t) {
    t2 = config !== undefined ? t("funded_of_goal", {
      funded: formatD6(funded, "compact"),
      goal: formatD6(config.creationThresholdD6, "compact")
    }) : null;
    $[4] = config;
    $[5] = formatD6;
    $[6] = funded;
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const fundedOfGoal = t2;
  let t3;
  if ($[9] !== props.numOutcomes || $[10] !== t) {
    t3 = props.numOutcomes > 0 ? t("outcomes_count", {
      count: props.numOutcomes
    }) : null;
    $[9] = props.numOutcomes;
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const outcomesPiece = t3;
  let t4;
  if ($[12] !== props.backerCount || $[13] !== t) {
    t4 = props.backerCount > 0 ? t("backers_count", {
      count: props.backerCount
    }) : null;
    $[12] = props.backerCount;
    $[13] = t;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const backersPiece = t4;
  let t5;
  if ($[15] !== fundedBig || $[16] !== goalBig) {
    t5 = fundedBig !== null && goalBig !== null && goalBig > BigInt(0) ? `${String(Math.round(Number(fundedBig * BigInt(100) / goalBig)))}%` : null;
    $[15] = fundedBig;
    $[16] = goalBig;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const pctFunded = t5;
  let t6;
  if ($[18] !== props) {
    t6 = () => {
      props.onHoverPrefetch?.(props.proposalId);
    };
    $[18] = props;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleHover = t6;
  const {
    onSelect,
    overlay
  } = useCommandLink(props.href, props.onCommit);
  const t7 = props.coverImageUrl.length > 0 ? props.coverImageUrl : null;
  let t8;
  if ($[20] !== props.coverImageNsfw || $[21] !== props.proposalId || $[22] !== t7) {
    t8 = <OptimizedImage variant="thumbnail" size="size-8" entity={{
      kind: "market",
      pubkey: props.proposalId,
      category: "",
      coverImageUrl: t7,
      nsfw: props.coverImageNsfw
    }} alt="" preview={false} />;
    $[20] = props.coverImageNsfw;
    $[21] = props.proposalId;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== props.marketType || $[25] !== t8) {
    t9 = <ThumbWithOrigin marketType={props.marketType} size="sm">{t8}</ThumbWithOrigin>;
    $[24] = props.marketType;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== props.title) {
    t10 = <span className="truncate text-body font-medium">{props.title}</span>;
    $[27] = props.title;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <EntityTag kind="launchpad" />;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t10) {
    t12 = <div className="flex min-w-0 items-center gap-2">{t10}{t11}</div>;
    $[30] = t10;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== props.ticker || $[33] !== props.tickerHighlighted) {
    t13 = tickerLabel(props.ticker) !== null ? <TickerText key="ticker" ticker={props.ticker} highlighted={props.tickerHighlighted} /> : null;
    $[32] = props.ticker;
    $[33] = props.tickerHighlighted;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== backersPiece || $[36] !== fundedOfGoal || $[37] !== outcomesPiece || $[38] !== t13) {
    t14 = <SearchRowSubline pieces={[t13, fundedOfGoal, outcomesPiece, backersPiece]} />;
    $[35] = backersPiece;
    $[36] = fundedOfGoal;
    $[37] = outcomesPiece;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t12 || $[41] !== t14) {
    t15 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t12}{t14}</div>;
    $[40] = t12;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== pctFunded || $[44] !== t) {
    t16 = pctFunded !== null ? <RowAside value={pctFunded} label={t("label_funded")} /> : null;
    $[43] = pctFunded;
    $[44] = t;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== handleHover || $[47] !== onSelect || $[48] !== overlay || $[49] !== props.proposalId || $[50] !== t15 || $[51] !== t16 || $[52] !== t9) {
    t17 = <CommandItem value={props.proposalId} onSelect={onSelect} onMouseEnter={handleHover} onFocus={handleHover} className="relative gap-3 rounded-overlay-item px-3 py-2.5">{overlay}{t9}{t15}{t16}</CommandItem>;
    $[46] = handleHover;
    $[47] = onSelect;
    $[48] = overlay;
    $[49] = props.proposalId;
    $[50] = t15;
    $[51] = t16;
    $[52] = t9;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  return t17;
}